import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { migrationApi } from '@/api/migration';

const LEGACY_PLANS = new Set(['basic', 'pro', 'business', 'custom']);
type FilterKey = 'all' | 'legacy' | 'pending';

export default function DiscoveryPage() {
  const [filter, setFilter] = useState<FilterKey>('legacy');

  const { data, isLoading } = useQuery({
    queryKey: ['migration', 'candidates'],
    queryFn: () => migrationApi.listCandidates(),
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    const all = data ?? [];
    if (filter === 'legacy') {
      return all.filter((r) => r.subscription && LEGACY_PLANS.has(r.subscription.plan_code));
    }
    if (filter === 'pending') {
      return all.filter((r) => !r.last_migration || r.last_migration.status === 'reverted');
    }
    return all;
  }, [data, filter]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Migration</h1>
          <p className="text-sm text-gray-400 mt-1">
            Move existing customers onto Jumpstart v3.2 tiers (Ignite / Accelerate / Command).
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        {(['legacy', 'pending', 'all'] as FilterKey[]).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className="px-3 py-1.5 rounded-lg text-sm capitalize"
            style={{
              background: filter === k ? '#6366f1' : '#1a1d27',
              color: filter === k ? 'white' : '#9ca3af',
              border: '1px solid #2a2d3e',
            }}
          >
            {k === 'legacy' ? 'On legacy plan' : k === 'pending' ? 'Not yet migrated' : 'All orgs'}
          </button>
        ))}
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}>
        <table className="w-full text-sm">
          <thead style={{ background: '#14161f' }}>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-3 font-medium">Organization</th>
              <th className="px-4 py-3 font-medium">Current plan</th>
              <th className="px-4 py-3 font-medium">Last migration</th>
              <th className="px-4 py-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No organizations match this filter.</td></tr>
            )}
            {rows.map((r) => {
              const legacy = r.subscription && LEGACY_PLANS.has(r.subscription.plan_code);
              return (
                <tr key={r.org.id} style={{ borderTop: '1px solid #2a2d3e' }}>
                  <td className="px-4 py-3 text-white">{r.org.name}</td>
                  <td className="px-4 py-3">
                    {r.subscription ? (
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs"
                        style={{
                          background: legacy ? '#f59e0b20' : '#6366f120',
                          color: legacy ? '#f59e0b' : '#a5b4fc',
                        }}
                      >
                        {r.subscription.system_code} / {r.subscription.plan_code}
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {r.last_migration
                      ? `${r.last_migration.status} · ${new Date(r.last_migration.created_at).toLocaleDateString()}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/migration/run/${r.org.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs"
                      style={{ background: '#6366f1', color: 'white' }}
                    >
                      Open wizard <ArrowRight size={14} />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
